import { useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import type { Player } from '@swiss-magic/shared-types';
import { rejectPlayer } from '@/lib/api/players';
import { ApiError } from '@/lib/api/core';
import { showErrorToast, showSuccessToast } from '@/lib/toast';

interface UseRejectDialogParams {
  tournamentId: string | undefined;
  setPlayers: Dispatch<SetStateAction<Player[]>>;
  setSelectedPlayer: Dispatch<SetStateAction<Player | null>>;
  onAfterReject?: (block: boolean) => void;
}

export function useRejectDialog({
  tournamentId,
  setPlayers,
  setSelectedPlayer,
  onAfterReject,
}: UseRejectDialogParams) {
  const { t } = useTranslation('director');
  const [rejectTarget, setRejectTarget] = useState<Player | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectBlock, setRejectBlock] = useState(false);
  const [submittingReject, setSubmittingReject] = useState(false);

  function openRejectDialog(player: Player) {
    setRejectTarget(player);
    setRejectReason('');
    setRejectBlock(false);
  }

  function closeRejectDialog() {
    setRejectTarget(null);
    setRejectReason('');
    setRejectBlock(false);
    setSubmittingReject(false);
  }

  async function handleSubmitReject() {
    if (!rejectTarget || !tournamentId) return;
    setSubmittingReject(true);
    try {
      const updated = await rejectPlayer(
        tournamentId,
        rejectTarget.id,
        rejectReason.trim() || null,
        rejectBlock,
      );
      setPlayers((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setSelectedPlayer((prev) => (prev?.id === updated.id ? updated : prev));
      showSuccessToast(
        rejectBlock
          ? t('players_page.reject_dialog.block_success')
          : t('players_page.reject_dialog.reject_success'),
      );
      onAfterReject?.(rejectBlock);
      closeRejectDialog();
    } catch (err) {
      if (err instanceof ApiError) showErrorToast(err.userMessage);
      else showErrorToast(t('players_page.reject_dialog.default_error'));
    } finally {
      setSubmittingReject(false);
    }
  }

  return {
    rejectTarget,
    openRejectDialog,
    closeRejectDialog,
    rejectReason,
    setRejectReason,
    rejectBlock,
    setRejectBlock,
    submittingReject,
    handleSubmitReject,
  };
}
